// Mock data for the Magic Signup prototype. Everything here is fictional — the
// prototype never calls a real scrape/RTRP pipeline. Scenarios mirror the
// cohorts described in the PRD (PR-5154): a business email whose domain is a
// direct scrape target, a consumer email whose prefix embeds the store name, a
// domain where we resolve a website but no Google Places listing, and a plain
// consumer email we can't infer anything from (standard flow).

export type ScenarioId =
  | "business-email"
  | "consumer-email"
  | "website-only"
  | "business-no-match"
  | "no-match";

/**
 * How much we managed to corroborate. A Google Places listing gives us photos,
 * a rating and an address; a website-only match gives us a name and a URL and
 * nothing else, so it has to be confirmed with plain fields.
 */
export type MatchSource = "google-places" | "website-only";

export interface StoreReview {
  author: string;
  rating: number;
  quote: string;
}

export interface StoreProfile {
  source: MatchSource;
  /** Store / business name we inferred from the domain. */
  storeName: string;
  /** Live website we resolved and corroborated. */
  website: string;
  /** Two-letter monogram used for deterministic photo fallbacks. */
  monogram: string;
  /** Short Google-Places-style place type shown on the rating line. */
  placeType?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  /** Google-Places-style aggregate rating (0–5). */
  rating?: number;
  /** Number of reviews backing the rating. */
  reviewCount?: number;
  /** Business photos rendered in the confirm-card collage. */
  photos?: string[];
  /** A couple of real-looking review snippets. */
  reviews?: StoreReview[];
}

export interface Scenario {
  id: ScenarioId;
  /** Label shown in the LiveToolbar scenario switcher. */
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  /** Registrable domain we would fire the scrape against. */
  domain: string;
  /**
   * Whether we'd actually run the domain scrape. Consumer mailbox domains
   * (gmail.com and friends) are never scraped, so those retailers see no
   * loading beat at all.
   */
  scrapesDomain: boolean;
  /** A corroborated profile, or null when there's nothing confident to confirm. */
  match: StoreProfile | null;
}

// ---------------------------------------------------------------------------
// Business type ("Which best describes your business?")
//
// Mirrors `ISignUpRequest.BusinessPlatform` and the copy in
// `@faire/onboarding-pages/lib/useBusinessTypeInfo`. Multi-select in
// production, so the form holds an array.
// ---------------------------------------------------------------------------

export type BusinessPlatform =
  | "BRICK_AND_MORTAR"
  | "ONLINE_ONLY"
  | "POP_UP"
  | "OTHER_PLATFORM";

export interface BusinessPlatformOption {
  value: BusinessPlatform;
  label: string;
  subtext?: string;
}

export const BUSINESS_PLATFORMS: BusinessPlatformOption[] = [
  {
    value: "BRICK_AND_MORTAR",
    label: "Brick and mortar location",
    subtext: "Store, restaurant, salon, etc.",
  },
  {
    value: "ONLINE_ONLY",
    label: "Online",
    subtext: "Website or social channel",
  },
  {
    value: "POP_UP",
    label: "Temporary location",
    subtext: "Markets, events, or pop-ups",
  },
  { value: "OTHER_PLATFORM", label: "Somewhere else" },
];

/**
 * Mirrors `getBusinessTypeDetails` in
 * `packages/signup/signup/src/views/SignUp/_utils/signupUtilities.ts`.
 */
export const getBusinessTypeDetails = (platforms: BusinessPlatform[]) => {
  const hasBrickAndMortar = platforms.includes("BRICK_AND_MORTAR");
  const hasPopup = platforms.includes("POP_UP");
  return {
    hasBrickAndMortar,
    hasOnline: platforms.includes("ONLINE_ONLY"),
    hasPopup,
    hasPhysicalStore: hasBrickAndMortar || hasPopup,
    hasPopUpNoBrickAndMortar: hasPopup && !hasBrickAndMortar,
  };
};

/**
 * Production rule from `StoreInfoForm`: the Google Places search input renders
 * for a physical store unless the only physical answer was "Temporary
 * location". In practice that means brick and mortar has to be selected —
 * online-only, temporary-only and "somewhere else" all get a plain text input.
 */
export const shouldShowPlacesSearch = (
  platforms: BusinessPlatform[]
): boolean => {
  const { hasPhysicalStore, hasPopUpNoBrickAndMortar } =
    getBusinessTypeDetails(platforms);
  return hasPhysicalStore && !hasPopUpNoBrickAndMortar;
};

/**
 * Production rule from `StoreInfoForm`: postal code is revealed for a physical
 * store that typed its name manually instead of picking a Places result, and
 * always for a temporary-location-only business (deliberate "good friction").
 */
export const shouldShowPostalCode = (
  platforms: BusinessPlatform[],
  isManualEntry: boolean
): boolean => {
  const { hasPhysicalStore, hasPopUpNoBrickAndMortar } =
    getBusinessTypeDetails(platforms);
  return (hasPhysicalStore && isManualEntry) || hasPopUpNoBrickAndMortar;
};

// ---------------------------------------------------------------------------
// Question options
// ---------------------------------------------------------------------------

// Matches `SIGNUP_REPORTED_CREATION_TIME_OPTIONS` from
// `@faire/open-with-faire-lib/utils/creationYearOptions` — "Opening soon",
// then the current year counting back five years, then a catch-all.
export const OPENING_YEARS = [
  "Opening soon",
  "2026",
  "2025",
  "2024",
  "2023",
  "2022",
  "2021",
  "Before 2021",
] as const;

export const OPENING_SOON_DATES = [
  "Within a month",
  "1 to 2 months",
  "2 to 4 months",
  "More than 4 months",
  "Not sure yet",
] as const;

export const BRICK_AND_MORTAR_OPENING_BUDGETS = [
  "Under $2,500",
  "$2,500 – $10,000",
  "$10,000 – $25,000",
  "Over $25,000",
  "Not sure yet",
] as const;

// ---------------------------------------------------------------------------
// Mock Google Places results for the business-name search input.
// ---------------------------------------------------------------------------

export interface PlacesSuggestion {
  name: string;
  location: string;
  postalCode: string;
}

const PLACES_SUGGESTIONS: PlacesSuggestion[] = [
  {
    name: "Jeff's Wrenches",
    location: "2140 S Lamar Blvd, Austin, TX",
    postalCode: "78701",
  },
  {
    name: "Created by Her Boutique",
    location: "412 Whitaker St, Savannah, GA",
    postalCode: "31401",
  },
  {
    name: "The Little Pantry",
    location: "88 Union St, Portland, ME",
    postalCode: "04101",
  },
  {
    name: "Wildflower Home Goods",
    location: "1500 NW 23rd Ave, Portland, OR",
    postalCode: "97210",
  },
  {
    name: "Bright Coffee Bar",
    location: "310 Grand Ave, Oakland, CA",
    postalCode: "94610",
  },
];

/** Naive substring filter — enough to make the dropdown feel live. */
export const findPlacesSuggestions = (query: string): PlacesSuggestion[] => {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length < 2) {
    return [];
  }
  return PLACES_SUGGESTIONS.filter((suggestion) =>
    suggestion.name.toLowerCase().includes(trimmed)
  ).slice(0, 3);
};

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------

export const SCENARIOS: Record<ScenarioId, Scenario> = {
  "business-email": {
    id: "business-email",
    label: "Business email — Places match",
    firstName: "Jeff",
    lastName: "Rivera",
    email: "jeff@jeffswrenches.com",
    domain: "jeffswrenches.com",
    scrapesDomain: true,
    match: {
      source: "google-places",
      storeName: "Jeff's Wrenches",
      website: "jeffswrenches.com",
      placeType: "Hardware store",
      city: "Austin",
      state: "TX",
      postalCode: "78701",
      monogram: "JW",
      rating: 4.8,
      reviewCount: 214,
      photos: [
        "https://images.unsplash.com/photo-1530124566582-a618bc2615dc?w=480&q=70&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=320&q=70&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1504148455328-c376907d081c?w=320&q=70&auto=format&fit=crop",
      ],
      reviews: [
        {
          author: "Marcus T.",
          rating: 5,
          quote:
            "Best selection of specialty tools in Austin. The staff actually knows their stuff.",
        },
        {
          author: "Dana R.",
          rating: 5,
          quote:
            "Came in for one wrench set and left with exactly what I needed. Great service.",
        },
      ],
    },
  },
  "consumer-email": {
    id: "consumer-email",
    label: "Consumer email — Places match",
    firstName: "Maya",
    lastName: "Osei",
    email: "createdbyherboutique@gmail.com",
    domain: "createdbyherboutique.com",
    scrapesDomain: true,
    match: {
      source: "google-places",
      storeName: "Created by Her Boutique",
      website: "createdbyherboutique.com",
      placeType: "Boutique",
      city: "Savannah",
      state: "GA",
      postalCode: "31401",
      monogram: "CH",
      rating: 4.9,
      reviewCount: 168,
      photos: [
        "https://images.unsplash.com/photo-1445205170230-053b83016050?w=480&q=70&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=320&q=70&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1567016432779-094069958ea5?w=320&q=70&auto=format&fit=crop",
      ],
      reviews: [
        {
          author: "Priya S.",
          rating: 5,
          quote:
            "Beautiful curated pieces you won't find anywhere else. My go-to for gifts.",
        },
        {
          author: "Alex M.",
          rating: 5,
          quote:
            "Lovely little shop with the friendliest owner. Always a treat to stop in.",
        },
      ],
    },
  },
  // We resolved a live site off the domain but found no Google Places listing,
  // so there are no photos, rating or address to show — only a name and a URL.
  "website-only": {
    id: "website-only",
    label: "Website only — no Places match",
    firstName: "Nadia",
    lastName: "Haddad",
    email: "nadia@thelittlepantry.co",
    domain: "thelittlepantry.co",
    scrapesDomain: true,
    match: {
      source: "website-only",
      storeName: "The Little Pantry",
      website: "https://thelittlepantry.co/",
      monogram: "LP",
    },
  },
  // A real business domain that the scrape comes back empty on. We still spent
  // the lookup time, so this cohort sees the same loading beat as a match and
  // then lands on the standard questions — no "we found nothing" screen.
  "business-no-match": {
    id: "business-no-match",
    label: "Business email — nothing found",
    firstName: "Priya",
    lastName: "Raman",
    email: "hello@stonefoxmercantile.com",
    domain: "stonefoxmercantile.com",
    scrapesDomain: true,
    match: null,
  },
  "no-match": {
    id: "no-match",
    label: "Consumer email — no scrape",
    firstName: "Jamie",
    lastName: "Chen",
    email: "jamie.buys.things@gmail.com",
    domain: "gmail.com",
    scrapesDomain: false,
    match: null,
  },
};

export const SCENARIO_ORDER: ScenarioId[] = [
  "business-email",
  "consumer-email",
  "website-only",
  "business-no-match",
  "no-match",
];

// Mailbox providers we'd never fire a scrape against. Anything else that looks
// like a real domain gets the lookup (and therefore the loading beat), even
// when it comes back empty.
const CONSUMER_EMAIL_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "aol.com",
  "me.com",
  "live.com",
  "msn.com",
  "protonmail.com",
  "proton.me",
];

/**
 * Resolves what we'd manage to scrape from a typed email, so the modal on the
 * first screen actually drives the flow instead of being decoration. Matches on
 * the whole address, then the domain, then the local part (the consumer-email
 * cohort hides the store name in the prefix). An unrecognised business domain
 * still gets scraped and comes back empty; a consumer mailbox is never scraped.
 */
export const resolveScenarioIdFromEmail = (email: string): ScenarioId => {
  const normalized = email.trim().toLowerCase();
  const [localPart = "", domain = ""] = normalized.split("@");

  const matchable = SCENARIO_ORDER.map((id) => SCENARIOS[id]).filter(
    (candidate) => candidate.match !== null
  );

  const exact = matchable.find(
    (candidate) => candidate.email.toLowerCase() === normalized
  );
  if (exact) {
    return exact.id;
  }

  const byDomain = matchable.find(
    (candidate) => candidate.domain.toLowerCase() === domain
  );
  if (byDomain) {
    return byDomain.id;
  }

  const byPrefix = matchable.find(
    (candidate) =>
      localPart.length > 0 && candidate.domain.startsWith(localPart)
  );
  if (byPrefix) {
    return byPrefix.id;
  }

  const isConsumerMailbox =
    domain.length === 0 || CONSUMER_EMAIL_DOMAINS.includes(domain);
  return isConsumerMailbox ? "no-match" : "business-no-match";
};

// ---------------------------------------------------------------------------
// Flow
// ---------------------------------------------------------------------------

export type StepId =
  | "email"
  | "personal"
  | "business-type"
  | "searching"
  | "confirm-store"
  | "opening-year"
  | "opening-soon-date"
  | "opening-budget"
  | "business-name"
  | "done";

// Every flow opens on the control-style "Unlock wholesale pricing" email modal.
// Business type runs before the lookup beat. Any scraped domain then gets the
// loading screen, whether or not the lookup finds anything; only a consumer
// mailbox skips it. Accepting a match skips business name. Declining, or finding
// no match, follows the standard order and asks business name last.
export const buildFlow = (
  scenario: Scenario,
  useMatch: boolean,
  openingYear: string,
  businessPlatforms: BusinessPlatform[]
): StepId[] => {
  const hasMatch = scenario.match !== null;
  const acceptedMatch = hasMatch && useMatch;
  const isOpeningSoon = openingYear === "Opening soon";
  const { hasBrickAndMortar } = getBusinessTypeDetails(businessPlatforms);

  const flow: StepId[] = ["email", "personal", "business-type"];
  if (scenario.scrapesDomain) {
    flow.push("searching");
  }
  if (hasMatch) {
    flow.push("confirm-store");
  }

  flow.push("opening-year");
  if (isOpeningSoon) {
    flow.push("opening-soon-date");
    if (hasBrickAndMortar) {
      flow.push("opening-budget");
    }
  }
  if (!acceptedMatch) {
    flow.push("business-name");
  }
  flow.push("done");
  return flow;
};

export const STEP_LABELS: Record<StepId, string> = {
  email: "Sign up",
  personal: "Personal info",
  searching: "Searching",
  "confirm-store": "Confirm store",
  "opening-year": "Opening year",
  "opening-soon-date": "Opening date",
  "opening-budget": "Opening budget",
  "business-type": "Business type",
  "business-name": "Business name",
  done: "Done",
};

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

export interface SignupForm {
  storeName: string;
  website: string;
  city: string;
  state: string;
  postalCode: string;
  openingYear: string;
  openingSoonDate: string;
  openingBudget: string;
  businessPlatforms: BusinessPlatform[];
  /** True once the retailer typed a name instead of picking a Places result. */
  isManualBusinessName: boolean;
}

const EMPTY_FORM = (): SignupForm => ({
  storeName: "",
  website: "",
  city: "",
  state: "",
  postalCode: "",
  openingYear: "",
  openingSoonDate: "",
  openingBudget: "",
  businessPlatforms: [],
  isManualBusinessName: false,
});

// Builds the form with factual identity fields from a confirmed match. Signup
// questions remain blank. A declined match returns a fully empty form.
export const buildForm = (
  scenario: Scenario,
  useMatch: boolean
): SignupForm => {
  if (!scenario.match || !useMatch) {
    return EMPTY_FORM();
  }
  const { match } = scenario;
  return {
    storeName: match.storeName,
    website: match.website,
    city: match.city ?? "",
    state: match.state ?? "",
    postalCode: match.postalCode ?? "",
    openingYear: "",
    openingSoonDate: "",
    openingBudget: "",
    businessPlatforms: [],
    isManualBusinessName: false,
  };
};
